import React from 'react';
import PropTypes from 'prop-types';
import { Chip, Box } from '@mui/material';
import BlockIcon from '@mui/icons-material/Block';
import TooltipMUI6 from 'src/components/TooltipMUI6';
import { getBlockedRegion, getBlockedRegionLabel } from 'src/constants/blockedRegions';

/**
 * LineTypeBadge
 *
 * Displays a badge indicating the phone line type (mobile, landline, VoIP, etc.)
 * with color-coding for textability and tooltip showing carrier and lookup date.
 */

// Badge configuration for each line type
// Maps line type to icon, color, and display label
const LINE_TYPE_CONFIG = {
  mobile: { icon: '📱', color: 'success', label: 'Mobile' },
  wireless: { icon: '📱', color: 'success', label: 'Mobile' },
  voip: { icon: '🌐', color: 'info', label: 'VoIP' },
  'fixed line or mobile': { icon: '📱', color: 'warning', label: 'Mobile?' },
  'personal number': { icon: '📱', color: 'success', label: 'Mobile' },
  'fixed line': { icon: '📞', color: 'error', label: 'Landline' },
  landline: { icon: '📞', color: 'error', label: 'Landline' },
  'toll free': { icon: '⚠️', color: 'warning', label: 'Toll-Free' },
  'premium rate': { icon: '⚠️', color: 'error', label: 'Premium' },
  premium: { icon: '⚠️', color: 'error', label: 'Premium' },
  other: { icon: '⚠️', color: 'default', label: 'Unknown' },
  // CON-2010 — a 5-6 digit brand short code (server sends lineType 'short code')
  'short code': { icon: '🚫', color: 'error', label: 'Short code' },
};

// CON-2010 — the same words the server writes on a blocked send
export const SHORT_CODE_REASON = 'Short code, can\'t be texted';

/**
 * Gets the badge configuration for a line type.
 * The displayLineType should already account for VoIP carrier override
 * (i.e., "fixed line" with VoIP carrier should be passed as "voip").
 */
function getBadgeConfig(lineType) {
  if (!lineType) return null;

  const normalizedType = lineType.toLowerCase();
  return LINE_TYPE_CONFIG[normalizedType] || LINE_TYPE_CONFIG.other;
}

/**
 * CON-1704 — builds the tooltip for a blocked-region number.
 *
 * Everything here is derived from the AREA CODE alone, so the badge renders
 * identically whether or not a carrier lookup exists — which matters,
 * because these rows usually have line_type null and often no carrier name
 * either. The carrier is appended only when we happen to have one.
 *
 * With a carrier it follows the existing "Type • Carrier • Status" shape:
 *   Trinidad & Tobago (868) • TELECOM TRIN/TOBAG • Can't receive your texts
 *     — likely a mistyped US number
 * Without one it says the whole thing in plain sentences instead.
 */
// Exported for tests: this repo has no render-testing library, so the
// blocked-region COPY is verified as a pure function and the chip itself is
// verified on screen.
export function buildBlockedRegionTooltip(areaCode, country, carrier) {
  const label = getBlockedRegionLabel(country);

  if (carrier) {
    return `${label} (${areaCode}) • ${carrier} • `
      + 'Can\'t receive your texts — likely a mistyped US or Canadian number';
  }

  // Two paragraphs, not one block: what is wrong, then what to do about it.
  // TooltipMUI6 sets whiteSpace: 'pre-line', so the blank line renders.
  //
  // Says "US and Canada", never "US" alone — CP has Canadian consultants and
  // supports every Canadian area code, and telling one of them her contact's
  // number is "not a US one" is both wrong and unhelpful.
  // 'an Anguilla area code', 'a Barbados area code'. Anguilla, Antigua &
  // Barbuda and Aruba are the vowel-initial labels in the list.
  const article = /^[AEIOU]/i.test(label) ? 'an' : 'a';

  return `Can't receive your texts — ${areaCode} is ${article} ${label} area code. `
    + 'CP can only text numbers in the US and Canada.'
    + '\n\n'
    + 'If this contact is in the US or Canada, the number is probably mistyped.';
}

/**
 * Builds the tooltip text based on line type, textability, and carrier info.
 * Format: "DisplayLineType • Carrier • Date • Status"
 */
export function buildTooltipText(displayLineType, isTextable, carrier, checkedAtRelative, lineType) {
  // CON-2010 — a short code has no carrier lookup; the reason is the whole story
  if (lineType?.toLowerCase() === 'short code') {
    return SHORT_CODE_REASON;
  }

  const parts = [];

  // Use displayLineType if provided, otherwise fall back to config label
  if (displayLineType) {
    parts.push(displayLineType);
  }

  // Add carrier (show "Unknown Carrier" if not available)
  parts.push(carrier || 'Unknown Carrier');

  // Add date if available
  if (checkedAtRelative) {
    parts.push(checkedAtRelative);
  }

  // Add status message for special cases
  const normalizedType = lineType?.toLowerCase();
  if (normalizedType === 'fixed line or mobile') {
    parts.push('May or may not receive texts');
  } else if (isTextable === false) {
    parts.push('Cannot receive texts');
  }

  return parts.join(' • ') || 'Phone type lookup';
}

function LineTypeBadge({
  lineType,
  displayLineType,
  isTextable,
  carrier,
  checkedAtRelative,
  onClick,
  phoneNumber = null,
}) {
  // CON-1704 — blocked NANP region takes precedence over line type: the number cannot
  // receive texts whatever the carrier lookup says about the line, and these
  // rows usually have no line type at all — which is why the badge was blank
  // on exactly the numbers that most needed one.
  const blockedRegion = getBlockedRegion(phoneNumber);
  if (blockedRegion.blocked) {
    return (
      <TooltipMUI6
        title={buildBlockedRegionTooltip(
          blockedRegion.areaCode,
          blockedRegion.country,
          carrier,
        )}
      >
        <Chip
          size="small"
          // An emoji 🚫 is itself red and disappeared against the red chip.
          // A filled MUI icon inherits the chip's contrast text colour, so it
          // reads as white-on-red at any size.
          icon={<BlockIcon sx={{ fontSize: '14px', color: 'inherit !important' }} />}
          label={getBlockedRegionLabel(blockedRegion.country)}
          color="error"
          onClick={onClick}
          clickable={!!onClick}
          sx={{
            ml: 1,
            height: '22px',
            '& .MuiChip-label': {
              px: 0.75,
              fontSize: '0.75rem',
            },
            '& .MuiChip-icon': {
              ml: 0.5,
              mr: -0.25,
            },
          }}
        />
      </TooltipMUI6>
    );
  }

  // Don't render if no line type data
  if (!lineType) {
    return null;
  }

  const config = getBadgeConfig(lineType);
  if (!config) {
    return null;
  }

  // Build tooltip content using new format
  const tooltipText = buildTooltipText(displayLineType, isTextable, carrier, checkedAtRelative, lineType);

  return (
    <TooltipMUI6 title={tooltipText}>
      <Chip
        size="small"
        icon={(
          <Box
            component="span"
            sx={{
              fontSize: '14px',
              lineHeight: 1,
              ml: 0.5,
            }}
          >
            {config.icon}
          </Box>
        )}
        label={config.label}
        color={config.color}
        onClick={onClick}
        clickable={!!onClick}
        sx={{
          ml: 1,
          height: '22px',
          '& .MuiChip-label': {
            px: 0.75,
            fontSize: '0.75rem',
          },
          '& .MuiChip-icon': {
            ml: 0.5,
            mr: -0.25,
          },
        }}
      />
    </TooltipMUI6>
  );
}

LineTypeBadge.propTypes = {
  lineType: PropTypes.string,
  displayLineType: PropTypes.string,
  isTextable: PropTypes.bool,
  carrier: PropTypes.string,
  checkedAtRelative: PropTypes.string,
  onClick: PropTypes.func,
  phoneNumber: PropTypes.string,
};

export default LineTypeBadge;
